<script setup lang="ts">
import type { FeedActivity, ReferenceStoreChangeOrigin } from '@/types/customer/ActivitiesFeed'
import ActivityCardBase from './ActivityCardBase.vue'

const { t } = useI18n()

const props = defineProps<{
  activity: FeedActivity
  showConnector?: boolean
}>()

const prefix = 'pages.customer.customerDetailPage.blocks.activity.referenceStoreChanged'

const AUTO_ORIGINS: ReferenceStoreChangeOrigin[] = ['checkin_rule', 'store_churned']

const titleLabel = computed(() => {
  const value = props.activity.additional_data?.origin
  if (!value) return t(`${prefix}.label`)

  if (value === 'partner') {
    const partner = props.activity.additional_data?.partner
    if (!partner) return t(`${prefix}.labelByOrigin.partnerUnknown`)
    return t(`${prefix}.labelByOrigin.partner`, { partner })
  }

  if (AUTO_ORIGINS.includes(value)) return t(`${prefix}.labelByOrigin.auto`)

  if (value === 'customer' || value === 'dashboard' || value === 'bulk') {
    return t(`${prefix}.labelByOrigin.${value}`)
  }

  return t(`${prefix}.label`)
})
</script>

<template>
  <ActivityCardBase
    icon="IconoirShop"
    icon-color="bg-teal-600"
    :occurred-at="activity.occurred_at"
    :show-connector="showConnector"
  >
    <template #title>
      <div class="flex w-full min-w-0 flex-1 flex-col items-stretch gap-3">
        <p class="text-sm text-muted-foreground" data-testid="reference-store-change-title">
          {{ titleLabel }}
        </p>
        <div class="flex w-full max-w-[min(20rem,100%)] flex-row items-stretch gap-1 sm:gap-1.5">
          <div
            class="flex min-w-0 flex-1 flex-col justify-center rounded-lg border border-border/90 bg-gradient-to-br from-muted to-muted/70 px-2 py-1.5"
          >
            <span class="mb-0.5 truncate text-[0.6rem] font-semibold uppercase tracking-wider text-muted-foreground">
              {{ t(`${prefix}.fromLabel`) }}
            </span>
            <span class="truncate text-xs font-semibold leading-snug text-foreground">{{
              activity.additional_data?.from_store
            }}</span>
          </div>

          <div class="flex shrink-0 items-center justify-center self-center px-0.5">
            <BaseIcon name="HeroArrowLongRightIconOutline" class="h-4 w-4 shrink-0 text-teal-600" aria-hidden="true" />
          </div>

          <div
            class="flex min-w-0 flex-1 flex-col justify-center rounded-lg border border-teal-300/70 bg-gradient-to-br from-teal-50 to-emerald-50/80 px-2 py-1.5 ring-1 ring-teal-100/80"
          >
            <span class="mb-0.5 truncate text-[0.6rem] font-semibold uppercase tracking-wider text-teal-800/85">
              {{ t(`${prefix}.toLabel`) }}
            </span>
            <span class="truncate text-xs font-semibold leading-snug text-foreground">{{
              activity.additional_data?.to_store
            }}</span>
          </div>
        </div>
      </div>
    </template>
  </ActivityCardBase>
</template>
